import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Phone, MapPin, Storefront, Trash, CalendarBlank, Archive, CheckCircle, XCircle, ArrowRight, Trophy, Plus, NotePencil, MagnifyingGlass, X, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, hasRole, hasCapability } from "@/context/AuthContext";
import { STAGES, PRIORITIES, TYPES, sellerColor, monthLabel, fmtDate } from "@/components/crm/constants";
import { PriorityBadge, ValueEditor } from "@/components/crm/CardEditors";
import { CardDetailDialog } from "@/components/crm/CardDetailDialog";
import { QuickAddBlock } from "@/components/crm/QuickAddBlock";
import { CalendarView } from "@/components/crm/CalendarView";

export default function CRM() {
  const { user } = useAuth();
  const canSeeAll = hasRole(user, "super_admin") || hasCapability(user, "crm_supervisor");
  const [board, setBoard] = useState(null);
  const [months, setMonths] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const dragRef = useRef(null);
  const [overStage, setOverStage] = useState(null);
  const [dragOver, setDragOver] = useState({ id: null, pos: null }); // {id, "above"|"below"}
  const [toDelete, setToDelete] = useState(null);
  const [detailCard, setDetailCard] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeSummary, setCloseSummary] = useState(null);
  const [view, setView] = useState("kanban"); // "kanban" | "calendar"
  const [calCursor, setCalCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [sellerFilter, setSellerFilter] = useState("all"); // supervisor-only filter
  const [searchQuery, setSearchQuery] = useState("");
  const [sellers, setSellers] = useState([]);

  // Load assignable sellers (only used when user can reassign — super_admin / supervisor)
  useEffect(() => {
    if (!canSeeAll) return;
    api.get("/sales/sellers").then((r) => setSellers(r.data || [])).catch(() => {});
  }, [canSeeAll]);

  const loadAll = async (month) => {
    const [b, m, s] = await Promise.all([
      api.get("/kanban", { params: month ? { month } : {} }),
      api.get("/kanban/months"),
      api.get("/kanban/summaries"),
    ]);
    setBoard(b.data);
    setMonths(m.data);
    setSummaries(s.data);
    if (!selectedMonth) setSelectedMonth(b.data.month);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (selectedMonth) loadAll(selectedMonth); }, [selectedMonth]);

  const handleDrop = async (e, stage, dropIndex = -1) => {
    e.preventDefault(); setOverStage(null); setDragOver({ id: null, pos: null });
    const cid = dragRef.current; dragRef.current = null;
    if (!cid || !board || board.is_archived) return;
    const card = board.cards.find((x) => x.id === cid);
    if (!card) return;
    // Build the target column's new order
    const destCards = byStage(stage).filter((x) => x.id !== cid);
    const pos = dropIndex === -1 ? destCards.length : Math.max(0, Math.min(dropIndex, destCards.length));
    const newOrderIds = [...destCards.slice(0, pos).map(c => c.id), cid, ...destCards.slice(pos).map(c => c.id)];
    // Optimistically update the board: mutate stage + assign position values
    setBoard((prev) => {
      if (!prev) return prev;
      const positionByCid = Object.fromEntries(newOrderIds.map((id, i) => [id, i * 1000]));
      return {
        ...prev,
        cards: prev.cards.map((x) => {
          if (x.id === cid) return { ...x, stage, position: positionByCid[cid] };
          if (positionByCid[x.id] !== undefined) return { ...x, position: positionByCid[x.id] };
          return x;
        }),
      };
    });
    try {
      const { data } = await api.post("/kanban/reorder", { stage, ordered_ids: newOrderIds });
      if (data?.ignored > 0 && data?.updated === 0) {
        // Backend rejected every card (typically because the user doesn't own them).
        // Warn and re-load so the optimistic move is undone.
        toast.error("No tienes permiso para mover esta tarjeta (pertenece a otro vendedor)");
        loadAll(selectedMonth);
      } else if (data?.ignored > 0) {
        toast.warning(`${data.ignored} tarjeta(s) no se reordenaron (sin permiso). Recarga si ves cambios inconsistentes.`);
      }
    } catch { toast.error("Error moviendo tarjeta"); loadAll(selectedMonth); }
  };

  const handleDeleteCard = async () => {
    if (!toDelete) return;
    try { await api.delete(`/kanban/cards/${toDelete.id}`); toast.success("Tarjeta eliminada"); loadAll(selectedMonth); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setToDelete(null); }
  };

  const updateCardField = async (cid, fields) => {
    setBoard((prev) => prev ? { ...prev, cards: prev.cards.map(x => x.id === cid ? { ...x, ...fields } : x) } : prev);
    try { await api.patch(`/kanban/cards/${cid}/fields`, fields); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error al actualizar"); loadAll(selectedMonth); }
  };

  const closeMonth = async () => {
    setShowCloseConfirm(false);
    try {
      const { data } = await api.post("/kanban/close");
      setCloseSummary(data);
      setSelectedMonth(data.next_month);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cerrar el mes");
    }
  };

  const byStage = (s) => {
    let all = board?.cards.filter((c) => c.stage === s) || [];
    // Always sort by `position` so optimistic reorder updates render in the new order.
    // Backend also returns cards sorted by position, but after we mutate positions
    // locally we need to re-sort here too.
    all = [...all].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    if (canSeeAll && sellerFilter !== "all") {
      all = sellerFilter === "__legacy__"
        ? all.filter((c) => !c.owner_user_id)
        : all.filter((c) => c.owner_user_id === sellerFilter);
    }
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => {
      const title = c.title || c.custom_title || c.client?.display_name || c.client?.name || "";
      const phone = c.client?.phone || "";
      const biz = c.client?.business_name || "";
      const city = c.client?.city || "";
      const notes = c.notes || "";
      const owner = c.owner_name || c.owner_email || "";
      return (
        title.toLowerCase().includes(q) ||
        phone.toLowerCase().includes(q) ||
        biz.toLowerCase().includes(q) ||
        city.toLowerCase().includes(q) ||
        notes.toLowerCase().includes(q) ||
        owner.toLowerCase().includes(q)
      );
    });
  };
  // List of sellers present in the current board (for supervisor filter)
  const sellersOnBoard = canSeeAll ? (() => {
    const m = new Map();
    for (const c of board?.cards || []) {
      if (c.owner_user_id) {
        if (!m.has(c.owner_user_id)) m.set(c.owner_user_id, { id: c.owner_user_id, name: c.owner_name || c.owner_email || "Vendedor", email: c.owner_email });
      }
    }
    return Array.from(m.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  })() : [];
  const hasLegacyOwnerless = canSeeAll && (board?.cards || []).some((c) => !c.owner_user_id);
  const isViewingActive = board && !board.is_archived;
  const currentSummary = summaries.find((s) => s.month === selectedMonth);
  const stageCounts = STAGES.reduce((acc, s) => ({ ...acc, [s.v]: byStage(s.v).length }), {});
  const stageTotals = STAGES.reduce((acc, s) => {
    const cs = byStage(s.v);
    const mxn = cs.filter(c => (c.value_currency || "MXN") === "MXN").reduce((sum, c) => sum + Number(c.transaction_value || 0), 0);
    const usd = cs.filter(c => (c.value_currency || "MXN") === "USD").reduce((sum, c) => sum + Number(c.transaction_value || 0), 0);
    return { ...acc, [s.v]: { mxn, usd } };
  }, {});
  const pipelineMXN = (stageTotals.lead?.mxn || 0) + (stageTotals.contacted?.mxn || 0);
  const pipelineUSD = (stageTotals.lead?.usd || 0) + (stageTotals.contacted?.usd || 0);
  const fmtNum = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="p-6 sm:p-8 space-y-5">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">PIPELINE MENSUAL</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">CRM</h1>
          <p className="text-zinc-500 mt-1 text-sm">
            Tablero <b>{monthLabel(selectedMonth || "")}</b>
            {board?.is_archived && <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">📁 Archivado · Solo lectura</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedMonth || ""} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-44 gap-2" data-testid="month-selector">
              <CalendarBlank size={14} className="text-zinc-500"/>
              <SelectValue placeholder="Mes…"/>
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.month} value={m.month}>
                  {monthLabel(m.month)} {m.archived ? "📁" : "•"}
                </SelectItem>
              ))}
              {months.length === 0 && board?.month && <SelectItem value={board.month}>{monthLabel(board.month)}</SelectItem>}
            </SelectContent>
          </Select>
          {isViewingActive && (
            <Button onClick={()=>setShowCloseConfirm(true)} data-testid="close-month-btn" className="bg-[#E0000F] hover:bg-[#b8000c] text-white gap-2">
              <Archive size={16} weight="bold" /> Cerrar mes
            </Button>
          )}
        </div>
      </header>

      {/* Stage counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stage-counters">
        {STAGES.map((s)=>(
          <div key={s.v} className="border border-zinc-200 p-3 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
            <div className="flex-1">
              <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">{s.label}</div>
              <div className="numeric text-xl font-bold" style={{ color: s.color }}>{stageCounts[s.v]}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Pipeline value summary (Seguimiento + En transacción) */}
      <div className="border border-[#002FA7]/30 bg-[#002FA7]/5 p-4 rounded-sm" data-testid="pipeline-value-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase font-mono-data tracking-wider text-[#002FA7]">Valor estimado en pipeline</div>
            <div className="text-xs text-zinc-500 mt-0.5">Suma de tarjetas en <b>Seguimiento</b> + <b>En transacción</b>.</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">Pesos mexicanos</div>
              <div className="numeric text-2xl font-bold text-[#002FA7]">MX${fmtNum(pipelineMXN)}</div>
            </div>
            <div className="w-px h-10 bg-[#002FA7]/20" />
            <div className="text-right">
              <div className="text-[10px] font-mono-data uppercase text-zinc-500">Dólares americanos</div>
              <div className="numeric text-2xl font-bold text-[#00873D]">US${fmtNum(pipelineUSD)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Archived summary banner */}
      {board?.is_archived && currentSummary && (
        <div className="border border-zinc-300 bg-zinc-50 p-4 rounded-sm flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">REPORTE DEL CIERRE</div>
            <div className="text-sm mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[#00873D] font-semibold"><CheckCircle size={14} weight="duotone"/> {currentSummary.won} ganados</span>
              <span className="inline-flex items-center gap-1 text-[#E0000F] font-semibold"><XCircle size={14} weight="duotone"/> {currentSummary.lost} perdidos</span>
              <span className="inline-flex items-center gap-1 text-zinc-600 font-semibold"><ArrowRight size={14}/> {currentSummary.moved_forward} movidos a {monthLabel(currentSummary.next_month)}</span>
            </div>
          </div>
          <Trophy size={32} weight="duotone" color="#FFC000" />
        </div>
      )}

      {/* View toggle */}
      <div className="flex gap-2 flex-wrap items-center">
        <button
          type="button"
          onClick={() => setView("kanban")}
          data-testid="crm-view-kanban"
          className={`px-4 py-2 rounded-sm border text-sm font-semibold transition ${view === "kanban" ? "bg-[#002FA7] border-[#002FA7] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
        >
          Kanban
        </button>
        <button
          type="button"
          onClick={() => setView("calendar")}
          data-testid="crm-view-calendar"
          className={`px-4 py-2 rounded-sm border text-sm font-semibold transition inline-flex items-center gap-2 ${view === "calendar" ? "bg-[#002FA7] border-[#002FA7] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
        >
          <CalendarBlank size={14} weight="bold"/> Calendario
        </button>
        {/* Search across cards */}
        <div className="relative flex-1 min-w-[200px] max-w-md" data-testid="crm-search">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" weight="bold"/>
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nombre, negocio, teléfono, notas o vendedor…"
            className="pl-8 pr-8 h-9 text-sm"
            data-testid="crm-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              aria-label="Limpiar"
              data-testid="crm-search-clear"
            >
              <X size={14} weight="bold"/>
            </button>
          )}
        </div>
        {canSeeAll && (sellersOnBoard.length > 0 || hasLegacyOwnerless) && (
          <div className="ml-auto flex items-center gap-2" data-testid="seller-filter">
            <User size={14} className="text-zinc-500" />
            <Select value={sellerFilter} onValueChange={setSellerFilter}>
              <SelectTrigger className="w-56" data-testid="seller-filter-trigger">
                <SelectValue placeholder="Todos los vendedores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los vendedores</SelectItem>
                {sellersOnBoard.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
                {hasLegacyOwnerless && <SelectItem value="__legacy__">Sin asignar (legado)</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Kanban */}
      {view === "kanban" && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kanban-board">
        {STAGES.map((s) => {
          const cards = byStage(s.v);
          const dropDisabled = board?.is_archived;
          return (
            <div
              key={s.v}
              onDragEnter={()=>setOverStage(s.v)}
              onDragOver={(e)=>{ e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDragLeave={()=>setOverStage(null)}
              onDrop={(e)=>!dropDisabled && handleDrop(e, s.v)}
              className={`flex flex-col bg-zinc-50 border-2 border-dashed rounded-sm min-h-[400px] transition-colors ${overStage===s.v && !dropDisabled ? "border-[#002FA7] bg-[#002FA7]/5" : "border-transparent"}`}
              data-testid={`col-${s.v}`}
            >
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-200">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{s.label}</div>
                    {(stageTotals[s.v]?.mxn > 0 || stageTotals[s.v]?.usd > 0) && (
                      <div className="text-[10px] numeric text-zinc-500 mt-0.5">
                        {stageTotals[s.v].mxn > 0 && <span>MX${fmtNum(stageTotals[s.v].mxn)}</span>}
                        {stageTotals[s.v].mxn > 0 && stageTotals[s.v].usd > 0 && <span className="mx-1 text-zinc-300">·</span>}
                        {stageTotals[s.v].usd > 0 && <span>US${fmtNum(stageTotals[s.v].usd)}</span>}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-xs font-mono-data text-zinc-500 shrink-0">{cards.length}</span>
              </div>
              <div className="p-2 space-y-2 flex-1">
                {cards.map((c, idx) => {
                  const cli = c.client;
                  const isFree = !cli;
                  const type = cli ? (TYPES.find((t)=>t.v===(cli.client_type||"final")) || TYPES[0]) : null;
                  const displayTitle = cli ? (cli.display_name || cli.name) : (c.title || "Sin nombre");
                  const isOver = dragOver.id === c.id;
                  return (
                    <div key={c.id} className="relative">
                      {isOver && dragOver.pos === "above" && (
                        <div className="absolute -top-1 left-0 right-0 h-0.5 bg-[#002FA7] rounded-full z-10" />
                      )}
                    <div
                      draggable={!dropDisabled}
                      onDragStart={(e)=>{
                        if (dropDisabled) return;
                        e.dataTransfer.effectAllowed = "move";
                        dragRef.current = c.id;
                      }}
                      onDragEnd={()=>setDragOver({ id: null, pos: null })}
                      onDragOver={(e)=>{
                        if (dropDisabled || dragRef.current === c.id) return;
                        e.preventDefault();
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const pos = (e.clientY - rect.top) < rect.height / 2 ? "above" : "below";
                        if (dragOver.id !== c.id || dragOver.pos !== pos) setDragOver({ id: c.id, pos });
                      }}
                      onDrop={(e)=>{
                        if (dropDisabled) return;
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const below = (e.clientY - rect.top) >= rect.height / 2;
                        handleDrop(e, s.v, below ? idx + 1 : idx);
                      }}
                      onClick={(e)=>{
                        // Ignore clicks on interactive children (buttons, inputs, selects)
                        if (e.target.closest("button, input, select, a, textarea")) return;
                        // Archived cards can still be opened in read-only mode
                        setDetailCard(c);
                      }}
                      className={`bg-white p-3 border border-zinc-200 rounded-sm ${dropDisabled ? "opacity-80" : "cursor-grab active:cursor-grabbing hover:border-[#002FA7] hover:shadow-sm"} transition-all ${canSeeAll && c.owner_user_id ? "border-l-4" : ""}`}
                      style={canSeeAll && c.owner_user_id ? { borderLeftColor: sellerColor(c.owner_user_id || c.owner_email) } : undefined}
                      data-testid={`card-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm truncate">{displayTitle}</div>
                          {cli && cli.business_name && (
                            <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5"><Storefront size={11}/> {cli.business_name}</div>
                          )}
                          {cli && cli.city && (
                            <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin size={11}/> {cli.city}</div>
                          )}
                          {cli && cli.phone && (
                            <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5"><Phone size={11}/> {cli.phone}</div>
                          )}
                          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                            <PriorityBadge
                              value={c.priority || ""}
                              disabled={dropDisabled}
                              onChange={(p)=>updateCardField(c.id, { priority: p })}
                            />
                            <ValueEditor
                              value={c.transaction_value || 0}
                              currency={c.value_currency || "MXN"}
                              disabled={dropDisabled}
                              onSave={(val, cur)=>updateCardField(c.id, { transaction_value: val, value_currency: cur })}
                            />
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={(e)=>{
                                e.stopPropagation();
                                if (dropDisabled) return;
                                const next = (c.value_currency || "MXN") === "MXN" ? "USD" : "MXN";
                                updateCardField(c.id, { value_currency: next });
                              }}
                              disabled={dropDisabled}
                              title="Click para cambiar moneda de la transacción"
                              className="text-[10px] font-mono-data px-1.5 py-0.5 bg-zinc-100 hover:bg-[#002FA7]/10 hover:text-[#002FA7] rounded transition-colors disabled:cursor-not-allowed"
                              data-testid={`currency-toggle-${c.id}`}
                            >
                              {c.value_currency || "MXN"}
                            </button>
                            {cli && type && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: type.color+"20", color: type.color }}>{type.label}</span>
                            )}
                            {isFree && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">Libre</span>
                            )}
                            {c.created_at && (
                              <span className="text-[10px] text-zinc-400 inline-flex items-center gap-0.5">
                                <CalendarBlank size={10}/> {fmtDate(c.created_at)}
                              </span>
                            )}
                            {c.scheduled_at && (
                              <span
                                className="text-[10px] font-semibold inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#7E57C2]/15 text-[#7E57C2]"
                                title={`Acción programada: ${new Date(c.scheduled_at).toLocaleString("es-MX")}`}
                                data-testid={`card-scheduled-${c.id}`}
                              >
                                <CalendarBlank size={10} weight="fill"/> {fmtDate(c.scheduled_at)}
                              </span>
                            )}
                            {c.notes && c.notes.trim() && (
                              <span className="text-[10px] text-[#002FA7] inline-flex items-center gap-0.5" title={c.notes}>
                                <NotePencil size={10}/> nota
                              </span>
                            )}
                            {canSeeAll && (c.owner_name || c.owner_email) && (
                              <span
                                className="text-[10px] font-semibold inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                                style={{ background: sellerColor(c.owner_user_id || c.owner_email) + "20", color: sellerColor(c.owner_user_id || c.owner_email) }}
                                title={`Vendedor: ${c.owner_name || c.owner_email}`}
                                data-testid={`card-seller-${c.id}`}
                              >
                                <User size={10} weight="fill"/> {c.owner_name || c.owner_email}
                              </span>
                            )}
                            {canSeeAll && !c.owner_user_id && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500" title="Sin vendedor asignado (legado)">
                                Sin asignar
                              </span>
                            )}
                          </div>
                        </div>
                        {!dropDisabled && (
                          <button onClick={(e)=>{ e.stopPropagation(); setToDelete(c); }} className="text-zinc-400 hover:text-[#E0000F]" data-testid={`del-card-${c.id}`} title="Eliminar tarjeta">
                            <Trash size={13}/>
                          </button>
                        )}
                      </div>
                    </div>
                      {isOver && dragOver.pos === "below" && (
                        <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#002FA7] rounded-full z-10" />
                      )}
                    </div>
                  );
                })}
                {cards.length === 0 && <div className="text-[11px] text-zinc-300 text-center py-2">—</div>}
                <QuickAddBlock
                  stage={s.v}
                  disabled={dropDisabled}
                  onCreated={(card)=> setBoard((prev)=> prev ? { ...prev, cards: [...prev.cards, { ...card, client: null }] } : prev)}
                />
              </div>
            </div>
          );
        })}
      </div>
      )}

      {view === "calendar" && (
        <CalendarView
          cards={(board?.cards || []).filter((c) => c.scheduled_at)}
          cursor={calCursor}
          setCursor={setCalCursor}
          onCardClick={setDetailCard}
        />
      )}

      {/* History of closed months */}
      {summaries.length > 0 && (
        <section className="border border-zinc-200 bg-white">
          <div className="px-6 py-3 border-b border-zinc-200 flex items-center gap-2">
            <Archive size={16} weight="duotone" />
            <h3 className="font-heading text-base font-semibold">Historial de cierres</h3>
          </div>
          <table className="w-full text-sm" data-testid="summaries-table">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Mes cerrado</th>
                <th className="text-right px-4 py-2">Ganados</th>
                <th className="text-right px-4 py-2">Perdidos</th>
                <th className="text-right px-4 py-2">Tasa de éxito</th>
                <th className="text-right px-4 py-2">Movidos al siguiente</th>
                <th className="text-left px-4 py-2">Cerrado el</th>
                <th className="text-right px-4 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {summaries.map((s) => {
                const total = s.won + s.lost;
                const rate = total > 0 ? Math.round((s.won / total) * 100) : 0;
                return (
                  <tr key={s.id || s.month} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2 font-semibold">{monthLabel(s.month)}</td>
                    <td className="px-4 py-2 text-right numeric text-[#00873D] font-semibold">{s.won}</td>
                    <td className="px-4 py-2 text-right numeric text-[#E0000F] font-semibold">{s.lost}</td>
                    <td className="px-4 py-2 text-right numeric font-semibold">{rate}%</td>
                    <td className="px-4 py-2 text-right numeric text-zinc-600">{s.moved_forward}</td>
                    <td className="px-4 py-2 text-zinc-600 text-xs">{new Date(s.closed_at).toLocaleString("es-MX")}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={()=>setSelectedMonth(s.month)} className="text-xs text-[#002FA7] hover:underline">Ver tablero →</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* Confirm close */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar mes {monthLabel(selectedMonth || "")}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción:
              <br />• Archivará los <b>{stageCounts.won}</b> ganados y <b>{stageCounts.lost}</b> perdidos en este mes (quedarán visibles en historial)
              <br />• Moverá los <b>{stageCounts.lead + stageCounts.contacted}</b> en seguimiento al nuevo mes
              <br />• Generará un reporte del cierre
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={closeMonth} className="bg-[#E0000F] hover:bg-[#b8000c] text-white" data-testid="confirm-close-month">Sí, cerrar mes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close summary */}
      <Dialog open={!!closeSummary} onOpenChange={(o)=>{ if (!o) setCloseSummary(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Trophy size={22} weight="duotone" color="#FFC000" /> Mes cerrado
            </DialogTitle>
          </DialogHeader>
          {closeSummary && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-600">El tablero de <b>{monthLabel(closeSummary.month)}</b> quedó archivado. Aquí está tu reporte:</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-[#00873D]/30 bg-[#00873D]/5 p-3 text-center">
                  <CheckCircle size={20} weight="duotone" color="#00873D" className="mx-auto"/>
                  <div className="numeric text-2xl font-bold text-[#00873D] mt-1">{closeSummary.won}</div>
                  <div className="text-[10px] uppercase font-mono-data text-zinc-600">Ganados</div>
                </div>
                <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 p-3 text-center">
                  <XCircle size={20} weight="duotone" color="#E0000F" className="mx-auto"/>
                  <div className="numeric text-2xl font-bold text-[#E0000F] mt-1">{closeSummary.lost}</div>
                  <div className="text-[10px] uppercase font-mono-data text-zinc-600">Perdidos</div>
                </div>
                <div className="border border-zinc-200 bg-zinc-50 p-3 text-center">
                  <ArrowRight size={20} weight="duotone" color="#52525B" className="mx-auto"/>
                  <div className="numeric text-2xl font-bold text-zinc-700 mt-1">{closeSummary.moved_forward}</div>
                  <div className="text-[10px] uppercase font-mono-data text-zinc-600">Movidos</div>
                </div>
              </div>
              <div className="text-xs text-zinc-500 text-center">Ahora estás en {monthLabel(closeSummary.next_month)}</div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={()=>setCloseSummary(null)} className="w-full bg-[#002FA7] hover:bg-[#002277] text-white">Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CardDetailDialog
        key={detailCard?.id || "none"}
        card={detailCard}
        readOnly={board?.is_archived || detailCard?.archived}
        canManageOwner={canSeeAll && !board?.is_archived && !detailCard?.archived}
        sellers={sellers}
        onReassign={async (newOwnerId) => {
          try {
            const { data } = await api.patch(`/kanban/cards/${detailCard.id}/owner`, { owner_user_id: newOwnerId });
            setBoard((prev) => prev ? { ...prev, cards: prev.cards.map((c) => c.id === detailCard.id ? { ...c, owner_user_id: data.owner_user_id, owner_email: data.owner_email, owner_name: data.owner_name } : c) } : prev);
            toast.success(`Reasignada a ${data.owner_name || data.owner_email}`);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al reasignar");
            throw err;
          }
        }}
        onClose={()=>setDetailCard(null)}
        onSave={async (fields) => {
          await updateCardField(detailCard.id, fields);
          setDetailCard((prev) => prev ? { ...prev, ...fields } : prev);
        }}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar tarjeta del tablero?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará la tarjeta de <b>{toDelete?.client?.display_name || toDelete?.client?.name || toDelete?.title || "esta tarjeta"}</b> de este mes. El cliente NO se elimina — sigue disponible en la sección Clientes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCard} className="bg-[#E0000F] hover:bg-[#b8000c] text-white">Quitar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
